import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import appointmentService from '../services/AppointmentService';
import treatmentRecordService from '../services/TreatmentRecordService';
import AppointmentForm from '../components/appointments/AppointmentForm';
import customerService from '../services/CustomerService';
import practitionerService from '../services/PractitionerService';
import venueService from '../services/VenueService';
import treatmentService from '../services/TreatmentService';
import workOrderService from '../services/WorkOrderService';
import {
  Card,
  Button,
  Modal,
  Alert
} from '../components/ui';
import { workOrderStatusLabels, treatmentRecordStatusLabels } from '../constants/statusLabels';
import Stepper from '../components/ui/Stepper';

/**
 * AppointmentDetailPage component for viewing and managing a specific appointment
 * @returns {React.ReactElement} AppointmentDetailPage component
 */
const AppointmentDetailPage = () => {
  // Get appointment id from URL params
  const { id: appointmentId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // State
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [treatmentRecords, setTreatmentRecords] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  // Reference data for display
  const [customer, setCustomer] = useState(null);
  const [treatment, setTreatment] = useState(null);
  const [practitioner, setPractitioner] = useState(null);
  const [venue, setVenue] = useState(null);

  // Check permissions
  const canViewAppointment = hasPermission('appointments', 'read');
  const canEditAppointment = hasPermission('appointments', 'write');
  const canCreateTreatmentRecord = hasPermission('treatmentRecords', 'write');

  // Fetch appointment data
  useEffect(() => {
    if (!appointmentId || !canViewAppointment) {
      console.log('Skipping appointment fetch:', { appointmentId, canViewAppointment });
      return;
    }

    const fetchAppointmentData = async () => {
      setLoading(true);
      try {
        console.log('Fetching appointment with ID:', appointmentId);

        // Get appointment
        const appointmentData = appointmentService.getAppointmentById(appointmentId);
        console.log('Retrieved appointment data:', appointmentData);

        if (!appointmentData) {
          console.log('No appointment found with ID:', appointmentId);
          setError('找不到预约记录');
          return;
        }

        // Map startTime to appointmentTime for display compatibility and calculate duration
        let duration = appointmentData.duration;
        if (!duration && appointmentData.startTime && appointmentData.endTime) {
          const start = new Date(appointmentData.startTime);
          const end = new Date(appointmentData.endTime);
          duration = Math.round((end - start) / 60000); // Convert ms to minutes
        }

        const processedAppointment = {
          ...appointmentData,
          appointmentTime: appointmentData.startTime || appointmentData.appointmentTime,
          duration: duration
        };

        setAppointment(processedAppointment);

        // Get all customers for the form
        const customersData = customerService.getAllCustomers();
        setCustomers(customersData);

        // Get related entities
        const customerData = customerService.getCustomerById(appointmentData.customerId);
        const treatmentData = treatmentService.getTreatmentById(appointmentData.treatmentId);
        const practitionerData = practitionerService.getPractitionerById(appointmentData.practitionerId);
        const venueData = venueService.getVenueById(appointmentData.venueId);

        setCustomer(customerData);
        setTreatment(treatmentData);
        setPractitioner(practitionerData);
        setVenue(venueData);

        // Get related treatment records
        const records = treatmentRecordService.getTreatmentRecordsByAppointment(appointmentId);
        setTreatmentRecords(records);

        // Fetch work orders for this appointment
        if (appointmentId) {
          const orders = workOrderService.getWorkOrdersByAppointmentId(appointmentId);
          setWorkOrders(orders);
        }

      } catch (err) {
        console.error('Error fetching appointment data:', err);
        setError('获取预约数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentData();
  }, [appointmentId, canViewAppointment]);

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '未设置';
    const date = new Date(dateString);

    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date for treatment records
  const formatDate = (dateString) => {
    if (!dateString) return '未记录';
    const date = new Date(dateString);

    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Status badge component
  const StatusBadge = () => {
    if (!appointment) return null;

    const statusClasses = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-red-100 text-red-800',
      noshow: 'bg-gray-100 text-gray-800'
    };

    const statusLabels = {
      scheduled: '已预约',
      confirmed: '已签到',
      completed: '已完成',
      cancelled: '已取消',
      noshow: '未到诊'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[appointment.status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[appointment.status] || appointment.status}
      </span>
    );
  };

  // Handle status change
  const handleStatusChange = (newStatus) => {
    try {
      appointmentService.updateAppointmentStatus(appointmentId, newStatus);
      // Get the latest appointment data after status update
      const updatedAppointment = appointmentService.getAppointmentById(appointmentId);
      setAppointment(updatedAppointment);
      // Auto-generate work order on check-in
      if (newStatus === 'confirmed') {
        const existingOrders = workOrderService.getWorkOrdersByAppointmentId(appointmentId);
        if (!existingOrders || existingOrders.length === 0) {
          workOrderService.createWorkOrder({
            appointmentId,
            customerId: updatedAppointment.customerId,
            practitionerId: updatedAppointment.practitionerId,
            treatmentProjectId: updatedAppointment.treatmentId,
            treatmentProjectName: treatment?.name,
            scheduledTime: updatedAppointment.appointmentTime,
            room: updatedAppointment.venueId,
            status: 'pending'
          });
          setWorkOrders(workOrderService.getWorkOrdersByAppointmentId(appointmentId));
        }
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('更新预约状态失败');
    }
  };

  // Handle delete appointment
  const handleDelete = () => {
    if (window.confirm('确定要删除此预约吗？此操作无法撤销。')) {
      try {
        appointmentService.deleteAppointment(appointmentId);
        navigate('/appointments');
      } catch (err) {
        console.error('Error deleting appointment:', err);
        alert('删除预约失败');
      }
    }
  };

  // Handle form submission
  const handleSubmit = (formData) => {
    setIsSubmitting(true);

    try {
      appointmentService.updateAppointment(appointmentId, formData);

      // Refresh appointment data
      const updatedAppointment = appointmentService.getAppointmentById(appointmentId);

      // Map startTime to appointmentTime for display compatibility and calculate duration
      let duration = updatedAppointment.duration;
      if (!duration && updatedAppointment.startTime && updatedAppointment.endTime) {
        const start = new Date(updatedAppointment.startTime);
        const end = new Date(updatedAppointment.endTime);
        duration = Math.round((end - start) / 60000); // Convert ms to minutes
      }

      const processedAppointment = {
        ...updatedAppointment,
        appointmentTime: updatedAppointment.startTime || updatedAppointment.appointmentTime,
        duration: duration
      };

      setAppointment(processedAppointment);

      // Refresh related entities
      const treatmentData = treatmentService.getTreatmentById(updatedAppointment.treatmentId);
      const practitionerData = practitionerService.getPractitionerById(updatedAppointment.practitionerId);
      const venueData = venueService.getVenueById(updatedAppointment.venueId);

      setTreatment(treatmentData);
      setPractitioner(practitionerData);
      setVenue(venueData);

      setShowEditForm(false);
    } catch (err) {
      console.error('Error updating appointment:', err);
      alert('更新预约失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle create treatment record
  const handleCreateTreatmentRecord = (order) => {
    navigate(`/treatment-records/new?workOrderId=${order.id}&appointmentId=${appointmentId}&customerId=${order.customerId}&treatmentProjectId=${order.treatmentProjectId}&treatmentProjectName=${encodeURIComponent(order.treatmentProjectName)}`);
  };

  // If there's an error finding the appointment
  if (error === '找不到预约记录') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card variant="modern" className="max-w-md w-full text-center">
          <div className="mb-6">
            <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3a2 2 0 012-2h6a2 2 0 012 2v4m-5 4v6m4-10v10a1 1 0 01-1 1H6a1 1 0 01-1-1V7a1 1 0 011-1h12a1 1 0 011 1z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              预约未找到
            </h2>
            <p className="text-gray-600">
              您尝试访问的预约记录不存在或已被删除
            </p>
          </div>
          <Button onClick={() => navigate('/appointments')}>
            返回预约列表
          </Button>
        </Card>
      </div>
    );
  }

  // If user doesn't have permission
  if (!canViewAppointment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card variant="modern" className="max-w-md w-full text-center">
          <div className="mb-6">
            <svg className="h-16 w-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              权限不足
            </h2>
            <p className="text-gray-600">
              您没有查看预约详情的权限
            </p>
          </div>
          <Button onClick={() => navigate('/')}>
            返回首页
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card variant="glass" className="border-emerald-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/appointments')}
                className="shrink-0"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                返回预约列表
              </Button>

              <div className="border-l border-gray-200 pl-4">
                {loading ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                ) : appointment ? (
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      预约详情
                    </h1>
                    <p className="text-gray-600">
                      {customer?.name || '未知客户'} 的预约信息
                    </p>
                  </div>
                ) : (
                  <h1 className="text-2xl font-bold text-gray-900">
                    预约详情
                  </h1>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {canEditAppointment && !showEditForm && appointment && (
                <>
                  <Button
                    onClick={() => setShowEditForm(true)}
                    className="shrink-0"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    编辑预约
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    删除预约
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Error Display */}
        {error && error !== '找不到预约记录' && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Card variant="modern">
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent"></div>
            </div>
          </Card>
        ) : showEditForm ? (
          /* Edit Form */
          <Card variant="modern">
            <div className="border-b border-gray-100 pb-4 mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                编辑预约信息
              </h3>
              <p className="text-gray-600 mt-1">
                修改预约的详细信息
              </p>
            </div>
            <AppointmentForm
              appointment={appointment}
              onSubmit={handleSubmit}
              onCancel={() => setShowEditForm(false)}
              isLoading={isSubmitting}
              customers={customers}
            />
          </Card>
        ) : appointment ? (
          /* Main Content: 预约信息和调理工单列表左右分布 */
          <div className="flex flex-col lg:flex-row gap-6">
            {/* 左侧：预约信息 */}
            <div className="lg:w-1/2 w-full">
              <Card variant="modern">
                <div className="border-b border-gray-100 pb-6 mb-6 flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      预约信息
                    </h3>
                    <p className="text-gray-600 mt-1">
                      预约详细信息和状态
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <StatusBadge />
                    {canEditAppointment && (
                      <select
                        value={appointment.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
                      >
                        <option value="scheduled">已预约</option>
                        <option value="confirmed">已签到</option>
                        <option value="completed">已完成</option>
                        <option value="cancelled">已取消</option>
                        <option value="noshow">未到诊</option>
                      </select>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-2">客户</label>
                      <div className="text-gray-900">
                        {customer ? (
                          <div>
                            <Link to={`/customers/${customer.id}`} className="text-emerald-600 hover:text-emerald-700 font-medium">
                              {customer.name}
                            </Link>
                            {customer.phoneNumber && (
                              <p className="text-sm text-gray-500 mt-1">
                                {customer.phoneNumber}
                              </p>
                            )}
                          </div>
                        ) : '未关联客户'}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-2">调理项目</label>
                      <div className="text-gray-900">
                        {treatment ? treatment.name : '未指定项目'}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-2">预约时间</label>
                      <div className="text-gray-900">
                        {formatDateTime(appointment.appointmentTime)}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-2">时长</label>
                      <div className="text-gray-900">
                        {appointment.duration ? `${appointment.duration} 分钟` : '未设定时长'}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-2">调理师</label>
                      <div className="text-gray-900">
                        {practitioner ? practitioner.name : '未指定调理师'}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-2">场地</label>
                      <div className="text-gray-900">
                        {venue ? (
                          <span>
                            {venue.name} {venue.location ? `(${venue.location})` : ''}
                          </span>
                        ) : '未指定场地'}
                      </div>
                    </div>
                  </div>

                  {appointment.notes && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 block mb-2">备注</label>
                      <div className="text-gray-900 bg-gray-50 rounded-xl p-4">
                        <p className="whitespace-pre-line">{appointment.notes}</p>
                      </div>
                    </div>
                  )}

                  {/* 支付信息区块 */}
                  {treatmentRecords && treatmentRecords.length > 0 && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-4">
                      <div className="font-semibold text-emerald-700 mb-2">支付信息</div>
                      {treatmentRecords.length === 1 ? (
                        (() => {
                          const s = treatmentRecords[0].settlement || {};
                          const paid = Number(s.paidAmount || 0);
                          const total = Number(s.totalAmount || 0);
                          return (
                            <div className="flex flex-col gap-1 text-sm">
                              <div>支付状态：<span className={paid >= total && total > 0 ? 'text-emerald-600' : 'text-gray-400'}>{paid >= total && total > 0 ? '已支付' : '未支付'}</span></div>
                              <div>实付金额：<span className="font-bold">{paid > 0 ? `¥${paid}` : '-'}</span></div>
                              <div>应付金额：{total > 0 ? `¥${total}` : '-'}</div>
                              {s.paymentMethod && <div>支付方式：{s.paymentMethod}</div>}
                            </div>
                          );
                        })()
                      ) : (
                        <div className="space-y-2">
                          {treatmentRecords.map((r, idx) => {
                            const s = r.settlement || {};
                            const paid = Number(s.paidAmount || 0);
                            const total = Number(s.totalAmount || 0);
                            return (
                              <div key={r.id} className="flex flex-col md:flex-row md:items-center gap-2 text-sm border-b last:border-b-0 pb-2 last:pb-0">
                                <span className="text-gray-500">调理记录{idx + 1}：</span>
                                <span>支付状态：<span className={paid >= total && total > 0 ? 'text-emerald-600' : 'text-gray-400'}>{paid >= total && total > 0 ? '已支付' : '未支付'}</span></span>
                                <span>实付金额：<span className="font-bold">{paid > 0 ? `¥${paid}` : '-'}</span></span>
                                <span>应付金额：{total > 0 ? `¥${total}` : '-'}</span>
                                {s.paymentMethod && <span>支付方式：{s.paymentMethod}</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* 设为已支付按钮，仅在存在未支付记录时显示 */}
                      {treatmentRecords.some(r => {
                        const s = r.settlement || {};
                        return Number(s.paidAmount || 0) < Number(s.totalAmount || 0);
                      }) && (
                        <button
                          className="mt-4 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow text-sm font-medium"
                          onClick={() => {
                            treatmentRecords.forEach(r => {
                              const s = r.settlement || {};
                              if (Number(s.paidAmount || 0) < Number(s.totalAmount || 0)) {
                                treatmentRecordService.updateTreatmentRecord(r.id, {
                                  settlement: { ...s, paidAmount: s.totalAmount }
                                });
                              }
                            });
                            window.location.reload();
                          }}
                        >
                          设为已支付
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
            {/* 右侧：调理工单列表 */}
            <div className="lg:w-1/2 w-full">
              <Card variant="modern">
                <div className="border-b border-gray-100 pb-6 mb-6 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">调理工单列表</h3>
                    <p className="text-gray-600 mt-1">与此预约关联的调理工单</p>
                  </div>
                </div>
                {workOrders.length > 0 ? (
                  <div className="space-y-6">
                    {workOrders.map(order => (
                      <div key={order.id} className="bg-gray-50 rounded-xl p-4">
                        {/* 工单进度条 */}
                        <Stepper
                          steps={[
                            { label: '待调理', value: 'pending' },
                            { label: '进行中', value: 'in_progress' },
                            { label: '已完成', value: 'completed' }
                          ]}
                          current={order.status}
                        />
                        <div>
                          <div className="font-medium text-emerald-700">调理工单ID: {order.id}</div>
                          <div className="text-xs text-gray-500 mt-1">创建时间: {formatDateTime(order.createdAt)}</div>
                        </div>
                        {/* 调理记录展示 */}
                        <div className="mt-4">
                          {order.treatmentRecordIds && order.treatmentRecordIds.length > 0 ? (
                            <div className="space-y-4">
                              {order.treatmentRecordIds.map(recordId => {
                                const record = treatmentRecords.find(r => r.id === recordId);
                                if (!record) return null;
                                // 获取调理师姓名
                                const practitionerObj = record.practitionerId
                                  ? practitionerService.getPractitionerById(record.practitionerId)
                                  : null;
                                // 获取摘要
                                const summary = record.tcmAssessment?.inquiry?.chiefComplaint
                                  || record.treatmentPlan?.planNotes
                                  || '';
                                return (
                                  <Link
                                    key={record.id}
                                    to={`/treatment-records/${record.id}`}
                                    className="block p-4 bg-white hover:bg-gray-100 rounded-xl transition-colors duration-200 border border-gray-100"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="text-sm font-medium text-emerald-600">
                                        {record.treatmentProjectName || '常规调理'}
                                      </h4>
                                      <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-800 rounded-full">
                                        {formatDate(record.date)}
                                      </span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 mb-2">
                                      <svg className="h-4 w-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                      </svg>
                                      {practitionerObj ? practitionerObj.name : '未记录调理师'}
                                    </div>
                                    {summary && (
                                      <p className="text-sm text-gray-600 line-clamp-2">
                                        {summary}
                                      </p>
                                    )}
                                  </Link>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-gray-400">
                              暂无调理记录
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8">无工单</div>
                )}
              </Card>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AppointmentDetailPage;